import { Icon } from '@buggy/shared'
import { Checkbox, Flex, Form, Modal, notification, Spin } from 'antd'
import { isNil } from 'lodash-es'
import { FunctionComponent, useRef, useState } from 'react'
import styled from 'styled-components'
import { nameof } from 'ts-simple-nameof'
import { useLazyFetchKolsUnlockStatusQuery } from '@/api/kols-api'
import {
  useAddPredictiveReportCandidateListMutation,
  useFetchPredictiveReportConfigQuery,
} from '@/api/workspace-api/predictive-report-api'
import { BasicModal } from '@/components/common/antd/basic-modal'
import KolThumbnailGroup from '@/components/common/kol-thumbnail-group'
import { collaborativeItemDefinitionList } from '@/components/predictive-report/constants/collaborative-item-definition'
import usePredictiveReportLimit from '@/components/predictive-report/hooks/use-predictive-report-limit'
import useUnlockCandidateWithModal from '@/components/predictive-report/hooks/use-unlock-candidate-with-modal'
import { PredictiveReportCandidateFormData } from '@/components/predictive-report/modals/common/candidate-modal'
import useIsQuotaBasedWorkspace from '@/hooks/use-is-quota-based-workspace'
import { useIntl } from '@/i18n/hooks/use-intl'
import { AddPredictiveReportCandidateListRequest } from '@/types/schema/api/predictive-report'
import {
  PredictiveReport,
  PredictiveReportConfig,
} from '@/types/schema/predictive-report'
import { AddKolToProposalProperties, ampli } from '@/utils/ampli'
import { convertPlatformShortcode } from '@/utils/convert-platform'
import getCooperationItemI18nKey from '@/utils/get-collaborative-item-i18n-key'
import logger from '@/utils/logger'
import { toVoidFunction } from '@/utils/to-void-function'

interface SelectCollaborativeItemsForKolModalProps {
  id: PredictiveReport['id']
  onClose: () => void
  candidateList?: PredictiveReportCandidateFormData[]
  from?: AddKolToProposalProperties['from']
  method?: AddKolToProposalProperties['method']
  open: boolean
  isClickedFromDrawer?: boolean
}

type FormData = {
  collaborative_items: string[]
}

const SelectCollaborativeItemsForKolModal: FunctionComponent<
  SelectCollaborativeItemsForKolModalProps
> = ({
  id,
  onClose,
  open,
  candidateList = [],
  from = 'Proposal',
  method,
  isClickedFromDrawer,
}) => {
  const { formatMessage } = useIntl()
  const [form] = Form.useForm<FormData>()
  const [loading, setLoading] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const isQuotaBasedWorkspace = useIsQuotaBasedWorkspace()
  const unlockCandidateWithModal = useUnlockCandidateWithModal()
  const [fetchKolsUnlockStatus] = useLazyFetchKolsUnlockStatusQuery()
  const [addPredictiveReportCandidateList] =
    useAddPredictiveReportCandidateListMutation()
  const { predictiveReportCollaborativeItemsLimit } = usePredictiveReportLimit()
  const { data: predictiveReportConfig, isLoading } =
    useFetchPredictiveReportConfigQuery(id)
  const [modal, contextHolder] = Modal.useModal()
  const collaborativeItemValue =
    Form.useWatch<FormData['collaborative_items']>(
      nameof<FormData>((p) => p.collaborative_items),
      form,
    ) ?? []

  const usedCount =
    (predictiveReportConfig?.used_collaborative_item_count ?? 0) +
    candidateList.length * collaborativeItemValue.length
  const isOver = usedCount > predictiveReportCollaborativeItemsLimit

  const handleAddCandidate: (
    predictiveReportConfig: PredictiveReportConfig,
    collaborativeItems: FormData['collaborative_items'],
  ) => Promise<boolean> = async (
    predictiveReportConfig,
    collaborativeItems,
  ) => {
    const candidateUuidList = candidateList.map((candidate) => candidate.uuid)
    if (isQuotaBasedWorkspace) {
      const candidateStatusList = (
        await fetchKolsUnlockStatus({
          kol_uuids: candidateUuidList,
        }).unwrap()
      ).data

      const hasAllUnlocked = await unlockCandidateWithModal(
        candidateStatusList
          .filter((status) => status.is_lock)
          .map((status) => status.kol_uuid),
      )

      if (!hasAllUnlocked) {
        return false
      }
    }

    await addPredictiveReportCandidateList({
      id: predictiveReportConfig.predictive_report_id,
      uuids: candidateUuidList,
      collaborative_items: collaborativeItems.map((item) => {
        const [platform, type] = item.split('_')
        return {
          is_custom_platform: false,
          platform_name: platform,
          is_custom_collaborative_item: false,
          collaborative_item: type,
        }
      }),
    }).unwrap()

    return true
  }

  const handleFinish = async (
    predictiveReportConfig: PredictiveReportConfig,
    formData: FormData,
  ): Promise<void> => {
    try {
      if (isOver) {
        modal.error({
          title: formatMessage(
            { id: 'general:modal_title_cooperation_content_limit' },
            { limitCount: predictiveReportCollaborativeItemsLimit },
          ),
          content: formatMessage({
            id: 'general:desc_limit_reached_contact_us',
          }),
        })
        ampli.reachProposalCollaborationCountLimit()
        return
      }

      setLoading(true)

      const hasAddSuccess = await handleAddCandidate(
        predictiveReportConfig,
        formData.collaborative_items,
      )
      if (!hasAddSuccess) {
        return
      }

      notification.success({
        message: formatMessage({ id: 'autoreport:report_updated' }),
        placement: 'bottomRight',
      })

      ampli.addKolToProposal({
        proposalId: predictiveReportConfig.predictive_report_id,
        proposalName: predictiveReportConfig.name,
        from,
        kolIds: candidateList.map((candidate) => candidate.uuid),
        kolNames: candidateList.map((candidate) => candidate.name),
        isClickedFromDrawer,
        method,
      })

      onClose()
    } catch (error) {
      notification.error({
        message: formatMessage({
          id: 'autoreport:report_update_failed',
        }),
        placement: 'bottomRight',
      })
      logger.error(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Wrapper ref={containerRef}>
      <BasicModal
        afterOpenChange={() => {
          form.resetFields()
        }}
        cancelText={formatMessage({ id: 'general:btn_text_cancel' })}
        footer={(_, { OkBtn, CancelBtn }) => (
          <>
            <ModalTip
              danger={isOver}
            >{`${formatMessage({ id: 'general:field_estimate_cooperation' })}${formatMessage({ id: 'general:colon' })}${usedCount}/${predictiveReportCollaborativeItemsLimit}`}</ModalTip>
            <CancelBtn />
            <OkBtn />
          </>
        )}
        getContainer={containerRef?.current ?? document.body}
        okButtonProps={{
          loading,
          disabled: isLoading || collaborativeItemValue.length === 0,
        }}
        okText={formatMessage({ id: 'vip:checkout_coupon_submit_btn' })}
        open={open}
        title={formatMessage({ id: 'general:field_name_cooperation_content' })}
        onCancel={(): void => {
          form.resetFields()
          onClose()
        }}
        onOk={(): void => {
          form.submit()
        }}
      >
        {isLoading || isNil(predictiveReportConfig) ? (
          <Spin />
        ) : (
          <Flex vertical gap={24}>
            <ModalDesc>
              {formatMessage({
                id: 'general:desc_select_platform_and_content',
              })}
            </ModalDesc>
            <KolList>
              <div>
                {formatMessage(
                  { id: 'general:number_of_kol' },
                  {
                    count: candidateList.length,
                  },
                )}
              </div>
              <KolThumbnailGroup
                data={candidateList.map(({ uuid }) => {
                  return {
                    uuid,
                  }
                })}
                from='Proposal'
                imageSize={32}
                maxCount={10}
              />
            </KolList>
            <Form
              form={form}
              layout='vertical'
              onFinish={toVoidFunction((formData) =>
                handleFinish(predictiveReportConfig, formData),
              )}
            >
              <Form.Item
                colon={false}
                label={formatMessage({
                  id: 'general:field_name_cooperation_content',
                })}
                name={nameof<AddPredictiveReportCandidateListRequest>(
                  (r) => r.collaborative_items,
                )}
                rules={[
                  {
                    required: true,
                    message: formatMessage(
                      { id: 'error:please_fulfill' },
                      {
                        inputField: formatMessage({
                          id: 'general:field_name_cooperation_content',
                        }),
                      },
                    ),
                  },
                ]}
              >
                <CooperationCardList>
                  {collaborativeItemDefinitionList.map(
                    ({ platformShortcode, items }) => {
                      const platformInfo =
                        convertPlatformShortcode(platformShortcode)

                      if (!platformInfo?.sharedIconName) {
                        return null
                      }

                      return (
                        <CooperationCard key={platformShortcode}>
                          <CooperationCardTitle>
                            <Icon
                              fontSize={16}
                              name={platformInfo.sharedIconName}
                            />
                            {platformInfo.title}
                          </CooperationCardTitle>
                          {items.map((item) => {
                            if (!item.hasCrawlerData) {
                              return null
                            }

                            return (
                              <Checkbox
                                key={item.type}
                                value={`${platformShortcode}_${item.type}`}
                              >
                                {formatMessage({
                                  id: getCooperationItemI18nKey(item.type),
                                })}
                              </Checkbox>
                            )
                          })}
                        </CooperationCard>
                      )
                    },
                  )}
                </CooperationCardList>
              </Form.Item>
            </Form>
          </Flex>
        )}
      </BasicModal>
      {contextHolder}
    </Wrapper>
  )
}

const ModalDesc = styled.div`
  font-size: 14px;
  line-height: 24px;
  color: ${({ theme }): string => theme.colors.text.secondary};
`

const ModalTip = styled.span<{ danger: boolean }>`
  font-weight: 400;
  font-size: 14px;
  line-height: 1.6;
  margin-right: 12px;
  color: ${({ danger, theme }): string =>
    danger ? theme.colors.base.red.red1 : theme.colors.text.tooltip};
`

const Wrapper = styled.div`
  position: absolute;

  .ant-modal-body {
    max-height: calc(100vh - 300px);
    overflow: auto;
  }

  .ant-form-item {
    margin: 0;
    width: 100%;
  }

  .ant-form-item-label {
    padding: 0;
    margin-bottom: 8px;
  }
`

const CooperationCardList = styled(Checkbox.Group)`
  display: flex;
  flex-wrap: nowrap;
  width: 100%;
  gap: 12px;
`

const CooperationCardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  padding: 4px 0;

  svg {
    font-size: 16px;
  }
`

const CooperationCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: ${({ theme }): string => theme.colors.background.grey};
  padding: 8px 12px;
  border-radius: 4px;

  .ant-checkbox-wrapper {
    height: 30px;
    align-items: center;
  }
`

const KolList = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 16px;
  line-height: 22px;
`

export type { SelectCollaborativeItemsForKolModalProps }
export default SelectCollaborativeItemsForKolModal
